import spartaEventsUtility from "utilities/global/sparta-events-utility/2.3.3/js/sparta-events-utility";
import jQuery from "utilities/vendor/jquery/3.5.1/js/jquery";
import $ from "utilities/vendor/jquery/3.5.1/js/jquery";
/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * @name        SPARTA Masking Utility
 * @file        sparta-masking-utility.js
 * @description Provides GIS masking logic for inputs and text values
 * @classdesc   Provides GIS masking logic for inputs and text values
 * @global      Creates a a global GISMasking object
 * @author      Kerry Boyce
 * @copyright   © 2016 Bank of America Corporation. All rights reserved.
 *
 * The GIS Masking library is used to create, present, and control the masking functionality
 * required by GIS to protect the privacy of customer sensitive personal information (SPI).  Any
 * HTML text inputs or text elements that contain SPI, must be tagged with the appropriate
 * "gis-mask" class to indicate that it needs masking and a "data-field-type" attribute
 * (described below) to identify any data-specific masking rules that need to be triggered.
 *
 * The currently supported "data-field-type" attribute values are:
 *     **no value** - will apply the default mask, which will mask all characters in the field
 *     email        - the mask for email addresses, which masks only the "local" part of the
 *                    email (i.e., the part before the "@").  How the local is mask is based on
 *                    it's length:
 *                       - for email addresses with local values fewer than 3 characters, all
 *                         characters in the local are masked
 *                       - for email addresses with local values with 3 or more characters, all
 *                         characters but the first and last ones in the local are masked
 *     phone        - the mask for phone numbers, which masks the first 6 digits in the phone
 *                    number
 *     account      - the mask for Bank of America account numbers, which masks all but the last
 *                    4 digits in the field
 *     date         - the mask for dates, which masks all alphanumeric characters in the field
 *     ssn          - the mask for social security numbers (SSN) and employer identification
 *                    numbers (EIN), which masks all alphanumeric characters in the field
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
; // prevent issues with other JS files omitting their last semi-colon

class SpartaMaskingUtility {
  init(getOptions = true) {
    const maskingClassBase = this.data.globals.MASKING_CLASS_BASE;

    /* apply the masking functionality to all fields tagged with the a class containing the value
       stored in "GISMasking.data.globals.MASKING_CLASS_BASE" */
    if (getOptions) {
      this.fn.core.addMasking($(`.${maskingClassBase}`));
    }
  }

  get data() {
    return data;
  }

  get fn() {
    return fn;
  }
}

const spartaMaskingUtility = new SpartaMaskingUtility();

/* GISMasking.data - library-level variables */
const data = {
  /* data.globals - configurable "constants" used throughout the library */
  globals: {
    /* the base masking class */
    PAGE_LANGUAGE: 'enUS',
    /* the base masking class */
    MASKING_CLASS_BASE: 'gis-mask',
    /* the original input masking class */
    MASKING_CLASS_ORIGINAL: 'gis-mask-original',
    /* the ready masking class */
    MASKING_CLASS_READY: 'gis-mask-value',
    /* suffix used to identify associated masking fields */
    MASKED_FIELD_SUFFIX: '-masked',
    /* the character to be displayed in place of  any masked characters.  "\u25CF" = (the "black
       circle" character) */
    MASKING_CHARACTER: '\u25CF',
    /* suffix used to identify the "full-masking toggle" checkbox */
    FULL_MASK_TOGGLE_SUFFIX: '-cbToggleFullMask',
    /* prefix used to create unique input re-masking timeouts in GISMasking.data.timeOuts */
    AUTO_RE_MASK_INPUT_PREFIX: 'remaskInputDelay_',
    /* prefix used to create unique text re-masking timeouts in GISMasking.data.timeOuts */
    AUTO_RE_MASK_TEXT_PREFIX: 'remaskTextDelay_',
    /* text used to communicate the "mask" action for screen readers */
    ADA_MASKING_TEXT: {
      enUS: '<span class="ada-hidden">This value has been hidden for security reasons</span>',
      esUS: '<span class="ada-hidden">Este valor ha sido ocultado por razones de seguridad</span>',
    },
    /* text used to communicate the "mask" action for screen readers */
    ADA_MASK_ACTION: {
      enUS: '<span aria-hidden="true" class="masking-action">Hide</span>',
      esUS: '<span aria-hidden="true" class="masking-action">Ocultar</span>',
    },
    /* text used to communicate the "unmask" action for screen readers */
    ADA_UNMASK_ACTION: {
      enUS: '<span class="ada-hidden">Activate link to</span> <span class="masking-action">Show</span> <span class="ada-hidden">this value (the value will be hidden again in 5 seconds)</span>',
      esUS: '<span class="ada-hidden">Activar vínculo para </span> <span class="masking-action">Mostrar</span> <span class="ada-hidden">este valor (el valor se ocultará otra vez en 5 segundos)</span>',
    },
  },
  /* data.maskingPatterns - standard regular expressions, representing the types of characters that
    will be masked in a field */
  maskingPatterns: {
    allChars: /./g,
    /* mask all character types */
    alphaNumeric: /[a-z\d]/gi, /* mask only alphanumeric characters */
  },
  timeOuts: {},
};

/* GISMasking.fn - library methods */
const fn = {
  /* "fn.helpers" methods - common "utility" functions that are used in multiple places in the
     library */
  helpers: {
    /* "helpers.applyMask" method
     * "applyMask" takes a string value and creates a masked version of it, using the standard
     * masking character.  How the mask is applied is determined by patterns passed in, indicating
     * what part of the string to mask and which characters to mask, within that sub-string.
     *
     * Parameters:
     *     sValue    - a string that contains the value that needs to be masked
     *     sPattern  - a string that contains the regular expression pattern to determine what part
     *                 of "sValue" should be masked
     *     sMaskType - a string that contains the "GISMasking.data.maskingPatterns" property that
     *                 represents the group of characters should be masked, within the sub-string
     *                 determined by "sPattern"
     *
     * Returns: a string value that contains the masked version of the original "sValue" string
     */
    applyMask(sValue, sPattern, sMaskType) {
      const aPatternMatch = sValue.match(new RegExp(sPattern));

      /* default the substring that should be masked to the entire string, in case the match
         fails */
      let sMaskedPortion = sValue;

      /* default the character types to match to all characters, in case the "sMaskType" value is
         invalid */
      let regexMaskPattern = spartaMaskingUtility.data.maskingPatterns.allChars;

      if (aPatternMatch) {
        sMaskedPortion = aPatternMatch[0];
      }

      if (Object.prototype.hasOwnProperty.call(
        spartaMaskingUtility.data.maskingPatterns, sMaskType
      )) {
        regexMaskPattern = spartaMaskingUtility.data.maskingPatterns[sMaskType];
      }

      /* mask the characters in the substring */
      const sMaskedValue = sMaskedPortion.replace(regexMaskPattern,
        spartaMaskingUtility.data.globals.MASKING_CHARACTER);

      /* return the original string value, with the appropriate characters masked */
      return sValue.replace(sMaskedPortion, sMaskedValue);
    },

    /* "helpers.hideElement" method
     * "hideElement" changes the visibility of an element, both visibly and to screen readers.
     */
    hideElement($element, bHideElement) {
      $element.toggleClass('spa-hidden', bHideElement).attr('aria-hidden', bHideElement);
    },
  },

  /* "fn.core" methods - the core functions in the library that set up the masked fields and
     activate the masking functionality, when triggered */
  core: {
    /* "core.addMasking" method
     * "addMasking" takes a group of inputs element and adds the GISMasking functionality to them.
     * The method cycles through each element in the group and, if it does not already have a paired
     * "masking field", creates one and adds the event listeners required to support the appropriate
     * masking patterns.
     *
     * Parameters:
     *     $inputGroup - a jQuery reference to a group of input element require the GISMasking
     *     functionality
     *
     * Returns: -none-
     */
    addMasking($maskingGroup) {
      /* set the PAGE_LANGUAGE global, if it does not match the page language */
      const sPageLanguage = $('html').attr('lang') ? $('html').attr('lang').replace(/[^a-z]/gi, '') :
        'enUS';

      if (spartaMaskingUtility.data.globals.PAGE_LANGUAGE !== sPageLanguage) {
        spartaMaskingUtility.data.globals.PAGE_LANGUAGE = sPageLanguage;
      }

      /* add input and text masking */
      spartaMaskingUtility.fn.core.addInputMasking($maskingGroup.filter(':input'));
      spartaMaskingUtility.fn.core.addTextMasking($maskingGroup.not(':input'));
    },

    /* "core.addInputMasking" method
     * "addInputMasking" takes a group of inputs element and adds the GISMasking functionality to
     * them.  The method cycles through each element in the group and, if it does not already have a
     * paired "masking field", creates one and adds the event listeners required to support the
     * appropriate masking patterns.
     *
     * Parameters:
     *     $inputGroup - a jQuery reference to a group of input element require the GISMasking
     *     functionality
     *
     * Returns: -none-
     */
    addInputMasking($inputGroup) {
      $inputGroup.each(function () {
        /* check to see if there is already a "masking field" paired with the current input */
        const $inputField = $(this);
        const sInputMaskId = $inputField.attr('id')
          + spartaMaskingUtility.data.globals.MASKED_FIELD_SUFFIX;
        let $inputMask = $(`#${sInputMaskId}`);

        /* if there is no masking field yet, create one, based on the current input */
        if ($inputMask.length === 0) {
          /* create a hidden copy of the current input and update the attributes to create the
               masking field */
          $inputMask = $inputField.clone();
          /* mark the original input field being masked so we can show it when masking is disabled.
           */
          $inputField.addClass(spartaMaskingUtility.data.globals.MASKING_CLASS_ORIGINAL);

          /* append the "MASKED_FIELD_SUFFIX" to the "id" and "name", to avoid any programmatic
             conflicts with the original field */
          $inputMask.attr('id', sInputMaskId)
            .attr('name', $inputField.attr('name') +
            spartaMaskingUtility.data.globals.MASKED_FIELD_SUFFIX)
            /* remove the masking class to avoid accidentally "reprocessing" the masking field,
               add the "gis-input-mask", to identify it as a masking field, and hide the field
               initially */
            .removeClass(spartaMaskingUtility.data.globals.MASKING_CLASS_BASE)
            .addClass(spartaMaskingUtility.data.globals.MASKING_CLASS_READY)

            /* add an event listener to display the original input, when the masking field
               receives focus */
            .bind('focus.gisMasking', function () {
              const $maskedField = $(this);
              const sMaskId = $maskedField.attr('id');

              /* if the field receives focus before the masking has been applied, cancel the
                 "maskInputData" call */
              const sRemaskId = spartaMaskingUtility.data.globals.AUTO_RE_MASK_INPUT_PREFIX
                + sMaskId;
              clearTimeout(spartaMaskingUtility.data.timeOuts[sRemaskId]);

              /* hide the masking field visually and from screen readers */
              spartaMaskingUtility.fn.helpers.hideElement($maskedField, true);

              /* display the input field visually and to screen readers and give it focus */
              const sSourceInputId = sMaskId
                .replace(spartaMaskingUtility.data.globals.MASKED_FIELD_SUFFIX, '');
              const $sourceInput = $(`#${sSourceInputId}`);

              spartaMaskingUtility.fn.helpers.hideElement($sourceInput, false);
              $sourceInput.focus().select();

              /* update the label to point to the original input */
              $(`label[for="${sMaskId}"]`).attr('for', sSourceInputId);
            })
            .bind('mousedown.gisMasking', function () {
              /* on the mousedown event trigger a full "click" event, since the field is hidden
                too fast to register it on its own */
              $(this).trigger('click');
            });

          /* initially hide the field visually and from screen readers */
          spartaMaskingUtility.fn.helpers.hideElement($inputMask, true);

          /* add the masking field to the DOM, after the original input */
          $inputField.after($inputMask);
        }

        /* add an event listener to display the masking field, with the masked data value, 0.2
           seconds after the original field loses focus */
        $(this).bind('blur.gisMasking', function () {
          spartaMaskingUtility.fn.core.maskInputData($(this), 200);
        });

        /* immediately display the masking field, with the masked data value, to mask any
           "pre-filled" data that may be in the input */
        spartaMaskingUtility.fn.core.maskInputData($(this), 0);
      });
    },

    /* "core.maskInputData" method
     * "maskInputData" applies the masking rules to the value of an input element and displays the
     * masked version in the associated "masking field", showing it in place of the original input.
     *
     * Parameters:
     *     $element - a jQuery reference to the input element that is being masked
     *     iDelay   - an integer representing the how long, in milliseconds, the method should wait
     *                before applying the mask
     *
     * Returns: -none-
     */
    maskInputData($element, iDelay) {
      const sCurrVal = $.trim($element.val());

      /* only apply the masking if the field is not empty and the value does not match the inputs
         "placeholder" attribute, if one exists (in place to handle IE-specific placeholder
         workarounds) */
      if ((sCurrVal !== '') && (sCurrVal !== $element.attr('placeholder'))) {
        /* retrieve the specific masking class for the input */
        const sMaskingType = $element.data('field-type');

        const sInputMaskId = $element.attr('id')
          + spartaMaskingUtility.data.globals.MASKED_FIELD_SUFFIX;
        const $inputMask = $(`#${sInputMaskId}`);

        /* default the masking function to the basic mask, in case the "sMaskingType" does not match
           one of the existing "GISMasking.fn.maskingRules" methods */
        let sMaskingFunctionName = 'basic';

        if (Object.prototype.hasOwnProperty.call(
            spartaMaskingUtility.fn.maskingRules, sMaskingType
        )) {
          sMaskingFunctionName = sMaskingType;
        }

        /* hide the input field visually and from screen readers */
        spartaMaskingUtility.fn.helpers.hideElement($element, true);

        /* update the label to point to the masking field */
        $(`label[for="${$element.attr('id')}"]`).attr('for', sInputMaskId);

        /* display the masking field visually and to screen readers */
        spartaMaskingUtility.fn.helpers.hideElement($inputMask, false);

        /* copy the current content of the input to the masking field */
        $inputMask.val(sCurrVal);

        /* after the delay specified by "iDelay" mask the value in the masking field */
        const sRemaskId = spartaMaskingUtility.data.globals.AUTO_RE_MASK_INPUT_PREFIX
          + sInputMaskId;
        spartaMaskingUtility.data.timeOuts[sRemaskId] = setTimeout(() => {
          $inputMask.val(spartaMaskingUtility.fn.maskingRules[sMaskingFunctionName](sCurrVal));
        }, iDelay);
      }
    },

    /* "core.addTextMasking" method
     * "addTextMasking" takes a group of inputs element and adds the GISMasking functionality to
     * them.  The method cycles through each element in the group and, if it does not already have a
     * paired "masking field", creates one and adds the event listeners required to support the
     * appropriate masking patterns.
     *
     * Parameters:
     *     $inputGroup - a jQuery reference to a group of input element require the GISMasking
     *     functionality
     *
     * Returns: -none-
     */
    addTextMasking($textGroup) {
      $textGroup.each(function () {
        /* check to see if there is already a "masking field" paired with the current input */
        const $textField = $(this);
        const $parentWrapper = $(this).closest('.masked-element');
        let $textMask = $parentWrapper
          .find(`.${spartaMaskingUtility.data.globals.MASKING_CLASS_READY}`);

        if ($textMask.length === 0) {
          /* create a hidden copy of the current field and update the attributes to create the
             masking field */
          $textMask = $textField.clone();

          /* mark the original input field being masked so we can show it when masking is
             disabled. */
          $textField.addClass(spartaMaskingUtility.data.globals.MASKING_CLASS_ORIGINAL);

          /* remove the masking class to avoid accidentally "reprocessing" the masking field, add
             the "MASKING_CLASS_READY" class, to identify it as a masking field, and hide the field
             initially */
          $textMask.removeClass(spartaMaskingUtility.data.globals.MASKING_CLASS_BASE)
            .addClass(spartaMaskingUtility.data.globals.MASKING_CLASS_READY);

          /* initially hide the field visually and from screen readers */
          spartaMaskingUtility.fn.helpers.hideElement($textMask, true);

          const sMaskingType = $textField.data('field-type');
          let sMaskingFunctionName = 'basic';

          if (Object.prototype.hasOwnProperty.call(
            spartaMaskingUtility.fn.maskingRules, sMaskingType
          )) {
            sMaskingFunctionName = sMaskingType;
          }

          /* copy the current content of the input to the masking field */
          $textMask.find('.masking-value')
            .text(spartaMaskingUtility.fn.maskingRules[sMaskingFunctionName](
              $textField.find('.masking-value').text()
            ));

          /* add the masking field to the DOM, after the original input */
          $textField.after($textMask);

          /* add the ADA text to the field and bind the masking event */
          $textField.append(spartaMaskingUtility.data.globals.ADA_MASK_ACTION[
            spartaMaskingUtility.data.globals.PAGE_LANGUAGE
          ]);

          $textField.bind('click.mask', function () {
            spartaMaskingUtility.fn.core.maskTextValue($textField);
          });

          /* hide the masked value from screen readers */
          const sCurrentValue = $textMask.html();
          $textMask.html(`<span aria-hidden='true'>${sCurrentValue}</span`);

          /* add the ADA text to the mask and bind the unmasking event */
          $textMask.prepend(spartaMaskingUtility.data.globals.ADA_MASKING_TEXT[
            spartaMaskingUtility.data.globals.PAGE_LANGUAGE
          ]);
          $textMask.append(spartaMaskingUtility.data.globals.ADA_UNMASK_ACTION[
            spartaMaskingUtility.data.globals.PAGE_LANGUAGE
          ]);

          $textMask.bind('click.unmask', function () {
            spartaMaskingUtility.fn.core.unmaskTextValue($textMask, 5000);
          });

          /* hide the initial value */
          spartaMaskingUtility.fn.core.unmaskTextValue($textMask, 0);
        }
      });
    },

    /* "core.maskTextValue" method
     * "maskTextValue" swaps the visibility between the masked and unmasked display text fields.
     */
    maskTextValue($element) {
      /* clear the re-mask timeout, if one is set */
      const sRemaskId = spartaMaskingUtility.data.globals.AUTO_RE_MASK_TEXT_PREFIX
        + $element.closest('.masked-element').attr('id');
      window.clearTimeout(spartaMaskingUtility.data.timeOuts[sRemaskId]);

      /* re-apply masking rules, in case the value has been updated */
      const sMaskingType = $element.data('field-type');
      let sMaskingFunctionName = 'basic';

      if (Object.prototype.hasOwnProperty.call(
        spartaMaskingUtility.fn.maskingRules, sMaskingType
      )) {
        sMaskingFunctionName = sMaskingType;
      }

      const $maskedField = $element.closest('.masked-element').find(
        `.${spartaMaskingUtility.data.globals.MASKING_CLASS_READY}`
      );
      $maskedField.find('.masking-value')
        .text(spartaMaskingUtility.fn.maskingRules[sMaskingFunctionName](
          $element.find('.masking-value').text()
        ));

      /* get the masked and unmasked elements */
      spartaMaskingUtility.fn.core.changeTextMask($element, true);
    },

    /* "core.unmaskTextValue" method
     * "unmaskTextValue" swaps the visibility between the masked and unmasked display text fields.
     */
    unmaskTextValue($element, iDelay) {
      /* get the masked and unmasked elements */
      spartaMaskingUtility.fn.core.changeTextMask($element, false);

      /* automatically re-mask the value after 5 seconds */
      const sRemaskId = spartaMaskingUtility.data.globals.AUTO_RE_MASK_TEXT_PREFIX
        + $element.closest('.masked-element').attr('id');
      spartaMaskingUtility.data.timeOuts[sRemaskId] = window.setTimeout(() => {
        spartaMaskingUtility.fn.core.changeTextMask($element, true);
      }, iDelay);
    },

    /* "core.changeTextMask" method
     * "changeTextMask" swaps the visibility between the masked and unmasked display text fields.
     */
    changeTextMask($element, bMaskValue) {
      const $parentWrapper = $element.closest('.masked-element');

      /* get the masked and unmasked elements */
      const $dataField = $parentWrapper.find(
        `.${spartaMaskingUtility.data.globals.MASKING_CLASS_ORIGINAL}`
      );
      const $maskedField = $parentWrapper.find(
        `.${spartaMaskingUtility.data.globals.MASKING_CLASS_READY}`
      );

      /* toggle the visibility of the elements */
      spartaMaskingUtility.fn.helpers.hideElement($dataField, bMaskValue);
      spartaMaskingUtility.fn.helpers.hideElement($maskedField, !bMaskValue);

      $parentWrapper.find('a:visible').focus();
    },
  },

  /* "fn.maskingRules" methods - the class specific functions that hold the logic and data values
     specific to each unique masking rule */
  maskingRules: {
    /* "maskingRules.basic" method
     * "basic" applies the basic masking rules to the current value.
     *
     * Parameters:
     *     sValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sValue" string, with the basic mask applied
     */
    basic(sValue) {
      const sInputPattern = '^.+$'; /* matches all characters in sValue */
      const sMaskingPattern = 'allChars';

      return spartaMaskingUtility.fn.helpers.applyMask(sValue, sInputPattern, sMaskingPattern);
    },

    /* "maskingRules.email" method
     * "email" applies the email-specific masking rules to the current value.
     *
     * Parameters:
     *     sEmailValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sEmailValue" string, with the "email" mask applied
     */
    email(sEmailValue) {
      /* the email mask is applied only to the "local" portion of the email address in
         sEmailValue */
      const sMaskedPortion = sEmailValue.split('@')[0];

      let sMaskedValue = sMaskedPortion;
      const sMaskedPortionLength = sMaskedPortion.length;

      /* if the local length is less than 3, mask all characters */
      if (sMaskedPortionLength < 3) {
        sMaskedValue = spartaMaskingUtility.fn.maskingRules.basic(sMaskedPortion);
      } else {
        /* if the local length is greater than 3, mask all characters except the first and last
           ones */
        const sSubstringPortion = sMaskedPortion.substring(1, (sMaskedPortionLength - 1));
        const sMaskedSubstringValue = spartaMaskingUtility.fn.maskingRules.basic(sSubstringPortion);

        sMaskedValue = sMaskedPortion.charAt(0) + sMaskedSubstringValue
          + sMaskedPortion.charAt(sMaskedPortionLength - 1);
      }

      return sEmailValue.replace(sMaskedPortion, sMaskedValue);
    },

    /* "maskingRules.phone" method
     * "phone" applies the phone-specific masking rules to the current value.
     *
     * Parameters:
     *     sPhoneValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sPhoneValue" string, with the "phone" mask applied
     */
    phone(sPhoneValue) {
      const sInputPattern = '^.+(?=.{4}$)'; /* matches all but the last 4 characters in sPhoneValue */
      const sMaskingPattern = 'alphaNumeric';

      return spartaMaskingUtility.fn.helpers.applyMask(sPhoneValue, sInputPattern, sMaskingPattern);
    },

    /* "maskingRules.account" method
     * "account" applies the account-specific masking rules to the current value.
     *
     * Parameters:
     *     sAccountValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sAccountValue" string, with the "account" mask
     *     applied
     */
    account(sAccountValue) {
      const sInputPattern = '^.+(?=.{4}$)'; /* matches all but the last 4 characters in sAccountValue */
      const sMaskingPattern = 'allChars';

      return spartaMaskingUtility.fn.helpers.applyMask(
        sAccountValue, sInputPattern, sMaskingPattern
      );
    },

    /* "maskingRules.date" method
     * "date" applies the date-specific masking rules to the current value.
     *
     * Parameters:
     *     sDateValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sDateValue" string, with the "date" mask applied
     */
    date(sDateValue) {
      const sInputPattern = '^.+$'; /* matches all characters in sDateValue */
      const sMaskingPattern = 'alphaNumeric';

      return spartaMaskingUtility.fn.helpers.applyMask(sDateValue, sInputPattern, sMaskingPattern);
    },

    /* "maskingRules.ssn" method
     * "ssn" applies the social security number/employer identification number-specific masking
     * rules to the current value.
     *
     * Parameters:
     *     sSSNValue - a string representing the data value to be masked
     *
     * Returns: a string value that contains the "sSSNValue" string, with the "ssn" mask applied
     */
    ssn(sSSNValue) {
      const sInputPattern = '^.+$'; /* matches all characters in sSSNValue */
      const sMaskingPattern = 'alphaNumeric';

      return spartaMaskingUtility.fn.helpers.applyMask(sSSNValue, sInputPattern, sMaskingPattern);
    },
  },
};

export default spartaMaskingUtility;
